/**
 * Shared @google/genai client + browser fetch patch.
 *
 * Single source of truth for the SDK instance + the proxy-auth wiring. The
 * fetch patch installs at MODULE LOAD, so any module that imports `ai` (or
 * `runGeminiJSON`) from here is guaranteed to have the patch in place
 * BEFORE its first request fires.
 *
 * Why this exists: every site that called `new GoogleGenAI({ httpOptions:
 * { baseUrl: '/api/gemini-proxy' } })` of its own (siteResearch.ts,
 * ComponentBuilderDialog.tsx) bypassed the patch and hit
 * `{ "error": "Authentication required" }` on the proxy. Centralising
 * removes the entire foot-gun.
 *
 * Patch behaviour:
 *   - Installed once per browser session (idempotent — guarded by
 *     `__geminiFetchPatched` on window).
 *   - Adds `Authorization: Bearer <firebaseIdToken>` to every request
 *     whose URL contains `/api/gemini-proxy`. Tokens are refreshed per
 *     request so the 1-hour Firebase token TTL doesn't strand long
 *     report runs.
 *   - Forwards the user's personal Gemini key via `x-da-user-key` when
 *     set (see src/lib/userKeyHeader.ts).
 *   - Captures the proxy's `X-DA-Key-Source` response header into
 *     `window.__lastGeminiKeySource` so geminiService.recordCall() can
 *     stamp telemetry.
 *   - Records a 429's `Retry-After` (lib/geminiProxyRateLimit) — the SDK
 *     error does not carry the header — so fan-out callers can wait out
 *     the proxy's per-IP window.
 *
 * VTX (row 0.99203) — backend selection via `GENAI_BACKEND` env:
 *   - `vertex`   → Vertex AI Gemini (production default). On the server
 *                  the SDK authenticates via ADC (Cloud Run runtime SA
 *                  with roles/aiplatform.user). In the browser the call
 *                  still routes through `/api/gemini-proxy/*`; the proxy
 *                  mints a Vertex OAuth bearer token and rewrites the
 *                  upstream URL to the Vertex publisher endpoint
 *                  (`${location}-aiplatform.googleapis.com/v1/projects/...`).
 *   - `aistudio` → legacy Gemini Developer API (AI Studio). Kept live
 *                  during the 7-day soak as a zero-redeploy rollback
 *                  (`gcloud run services update --update-env-vars=GENAI_BACKEND=aistudio`).
 *
 * The SDK call shape (`ai.models.generateContent({...})`) is identical
 * between backends, so the 95 call sites across 17 files keep their
 * existing structure unchanged. See `docs/runbooks/gemini-vertex.md`.
 */
import { GoogleGenAI, Type, type Schema } from '@google/genai';
import { GEMINI_PRO_MODEL } from '../config/geminiModels';
import { vertexLocationForModel } from '../config/geminiLocations';
import { getGenaiBackend } from '../lib/runtimeFlags';
import { withGenAIRetry, type GenAIRetryOptions } from './genaiRetry';
import { normalizeArgonRequest, assertArgonResponseComplete, guardArgonStream } from './gemini4Compatibility';
import { throwIfAborted } from '../lib/abortSignal';
import { noteGeminiProxyRateLimited } from '../lib/geminiProxyRateLimit';
import {
  measuredGeminiTokenUsage,
  type GeminiMeasuredTokenUsage,
} from '../lib/billing/geminiUsage';

declare const __GOOGLE_CLOUD_PROJECT__: string;
declare const __GOOGLE_CLOUD_LOCATION__: string;

// VTX-1 — backend selection. Server/tests read `process.env.GENAI_BACKEND`.
// Browser bundle falls back to Vite's `__GENAI_BACKEND__` define so the SDK
// uses Vertex call shapes (`:predict` / `:embedContent`) when the proxy does.
function resolveGenaiBackend(): 'aistudio' | 'vertex' {
  if (typeof process !== 'undefined' && process.env?.GENAI_BACKEND) {
    return process.env.GENAI_BACKEND === 'aistudio' ? 'aistudio' : 'vertex';
  }
  return getGenaiBackend();
}

const BACKEND = resolveGenaiBackend();

function readCloudProject(): string {
  if (typeof process !== 'undefined' && process.env?.GOOGLE_CLOUD_PROJECT) {
    return process.env.GOOGLE_CLOUD_PROJECT;
  }
  try {
    if (typeof __GOOGLE_CLOUD_PROJECT__ === 'string' && __GOOGLE_CLOUD_PROJECT__.length > 0) {
      return __GOOGLE_CLOUD_PROJECT__;
    }
  } catch { /* outside Vite bundle */ }
  return 'datacrate-btm-b5edd';
}

function readCloudLocation(): string {
  if (typeof process !== 'undefined' && process.env?.GOOGLE_CLOUD_LOCATION) {
    return process.env.GOOGLE_CLOUD_LOCATION;
  }
  try {
    if (typeof __GOOGLE_CLOUD_LOCATION__ === 'string' && __GOOGLE_CLOUD_LOCATION__.length > 0) {
      return __GOOGLE_CLOUD_LOCATION__;
    }
  } catch { /* outside Vite bundle */ }
  return 'us-central1';
}

const GCP_PROJECT = readCloudProject();
const GCP_LOCATION = readCloudLocation();

/**
 * SDK cancellation deadline. Native fetch also has an independent 300s
 * headers deadline; the server facade aligns that transport deadline for
 * each generation request without changing the process-wide dispatcher.
 */
const SERVER_GENAI_TIMEOUT_MS: number = (() => {
  const raw =
    typeof process !== 'undefined' ? Number(process.env?.GENAI_REQUEST_TIMEOUT_MS) : NaN;
  return Number.isFinite(raw) && raw > 0 ? raw : 480_000;
})();

/**
 * Per-model Vertex location routing.
 *
 * Vertex AI availability is SPLIT across locations, so a single
 * `GOOGLE_CLOUD_LOCATION` cannot serve every stage: the gemini-3.x families
 * (Pro, Flash, Flash-Lite) answer only at `global`, while the configured base
 * (`us-central1` in prod) keeps serving the 2.5 line, embeddings and unknown
 * models. The rule and the probe table behind it live in
 * `config/geminiLocations.ts` so the DAM client shares it.
 *
 * Callers (browser proxy) can inject their own base location.
 */
export function locationForModel(model: string, baseLocation: string = GCP_LOCATION): string {
  return vertexLocationForModel(model, baseLocation);
}

// Server-side: use GEMINI_API_KEY directly (aistudio) or ADC (vertex). Browser:
// route through the /api/gemini-proxy server route, which injects the real
// key (aistudio) or mints a Vertex OAuth bearer (vertex). The placeholder
// keeps the SDK from emitting "API key should be set" at module load.
const isServerRuntime = typeof window === 'undefined';
const hasServerGeminiKey = typeof process !== 'undefined' && !!process.env?.GEMINI_API_KEY;
const geminiKey = hasServerGeminiKey
  ? process.env.GEMINI_API_KEY!
  : 'AIzaSyProxy_NotUsed_RoutedViaServer';
const proxyBaseUrl = typeof window !== 'undefined'
  ? `${window.location.origin}/api/gemini-proxy`
  : `http://localhost:${process.env.PORT || '3000'}/api/gemini-proxy`;

/**
 * Run `fn` with the SDK's "API key should be set" warning suppressed (the key
 * is injected server-side by `/api/gemini-proxy`, so the warning is noise).
 *
 * This was previously a PERMANENT `console.warn` wrapper installed at module
 * load. DevTools attributes a console record to the JS frame that called the
 * native method, so that wrapper made EVERY warning in the app report its
 * origin as this file — e.g. genuine `[subscribeReportGenerationJobs]` warnings
 * raised in `src/store/index.ts` all rendered as `geminiClient.ts:170`, which
 * sends anyone triaging the console to the wrong file entirely.
 *
 * Scoping the wrapper to the one browser construction site keeps the
 * suppression and restores correct attribution everywhere else. `finally`
 * guarantees a throwing constructor cannot leak the wrapper back to global
 * scope.
 */
function withApiKeyWarningSuppressed<T>(fn: () => T): T {
  if (isServerRuntime || typeof console === 'undefined') return fn();
  const origWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].includes('API key')) return;
    origWarn.apply(console, args);
  };
  try {
    return fn();
  } finally {
    console.warn = origWarn;
  }
}

if (typeof window !== 'undefined' && !(window as unknown as { __geminiFetchPatched?: boolean }).__geminiFetchPatched) {
  const origFetch = window.fetch.bind(window);
  const patched = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input
      : input instanceof URL ? input.toString()
      : input.url;
    if (url.includes('/api/gemini-proxy')) {
      try {
        const { auth } = await import('../firebase');
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
          headers.set('Authorization', `Bearer ${token}`);
          // Attach the user's personal Gemini key when set so the proxy uses
          // it instead of the org Secret Manager key. The proxy returns
          // X-DA-Key-Source: 'user' | 'org' which the recorder consults
          // when stamping telemetry.
          try {
            const { buildGeminiUserKeyHeader, getCurrentUserApiKeys } = await import('../lib/userKeyHeader');
            const personalHeader = buildGeminiUserKeyHeader(getCurrentUserApiKeys());
            if (personalHeader) headers.set('x-da-user-key', personalHeader);
          } catch { /* no store / no personal key — fall through to org */ }
          const res = await origFetch(input, { ...init, headers });
          // The proxy's per-IP bucket is spent: keep its Retry-After, which
          // the SDK error drops, so fan-out callers (DM2 doc matching) wait
          // it out instead of firing into the closed bucket.
          if (res.status === 429) noteGeminiProxyRateLimited(res.headers.get('Retry-After'));
          // Cache the most recent key source so recordCall() can stamp
          // telemetry. Race-acceptable: Gemini calls are awaited
          // sequentially per stage.
          const src = res.headers.get('X-DA-Key-Source');
          if (src === 'user' || src === 'org') {
            (window as unknown as { __lastGeminiKeySource?: 'user' | 'org' }).__lastGeminiKeySource = src;
          }
          return res;
        }
      } catch {
        // Fall through to the original fetch — better to surface the
        // upstream 401 than to silently swallow an auth-token error.
      }
    }
    return origFetch(input, init);
  };
  window.fetch = patched as typeof window.fetch;
  (window as unknown as { __geminiFetchPatched?: boolean }).__geminiFetchPatched = true;
}

/**
 * VTX — backend descriptor exported so server code (server.ts proxy +
 * /api/health/gemini probe) and the model-resolver in geminiService can
 * branch consistently without each one re-reading process.env.
 */
export const GENAI_BACKEND: 'aistudio' | 'vertex' = BACKEND;
export const VERTEX_PROJECT = GCP_PROJECT;
export const VERTEX_LOCATION = GCP_LOCATION;
export const DOC_OCR_BACKEND: 'vision-primary' | 'gemini-only' = (() => {
  const raw = (typeof process !== 'undefined' && process.env?.VITE_DOC_OCR_BACKEND)
    || (typeof process !== 'undefined' && process.env?.DOC_OCR_BACKEND)
    || 'vision-primary';
  return String(raw).trim().toLowerCase() === 'gemini-only' ? 'gemini-only' : 'vision-primary';
})();

/**
 * Construct the SDK singleton. Branches on `GENAI_BACKEND`:
 *   - `vertex` server  → ADC via Cloud Run runtime SA (no apiKey).
 *   - `vertex` browser → still hits `/api/gemini-proxy`; the proxy mints
 *                        the Vertex bearer token + rewrites the URL.
 *                        SDK requires a truthy apiKey to construct so we
 *                        pass an explicit placeholder.
 *   - `aistudio`       → legacy: GEMINI_API_KEY on the server or proxy
 *                        injection in the browser.
 */
function makeGenAI(): GoogleGenAI {
  // The SDK emits its "API key should be set" warning during construction, so
  // the suppression wraps exactly this call and is torn down immediately after.
  return withApiKeyWarningSuppressed((): GoogleGenAI => {
    if (BACKEND === 'vertex') {
      if (isServerRuntime) {
        return new GoogleGenAI({
          vertexai: true,
          project: GCP_PROJECT,
          location: GCP_LOCATION,
          httpOptions: { timeout: SERVER_GENAI_TIMEOUT_MS },
        });
      }
      // Browser path — route via proxy. The SDK forbids project/location in
      // browser runtimes; `/api/gemini-proxy` rewrites upstream paths to the
      // deployment project + location (global for gemini-3.1-pro-preview).
      return new GoogleGenAI({
        vertexai: true,
        apiKey: 'BROWSER_PROXY_PLACEHOLDER_NOT_USED',
        httpOptions: { baseUrl: proxyBaseUrl },
      });
    }
    // aistudio (legacy, soak fallback)
    return new GoogleGenAI({
      apiKey: geminiKey,
      ...(!isServerRuntime
        ? { httpOptions: { baseUrl: proxyBaseUrl } }
        : { httpOptions: { timeout: SERVER_GENAI_TIMEOUT_MS } }),
    });
  });
}

/**
 * SERVER-side Vertex per-model location routing.
 *
 * A single GoogleGenAI client is bound to one `location` at construction, but
 * Vertex availability is split (see `locationForModel`): the report-narrative
 * Pro line lives only in `global` while document-extraction Flash lives only
 * in the regional base. So on the server we build ONE client PER LOCATION
 * (lazily, cached in `serverClientsByLocation`) and dispatch each
 * `models.<method>` call to the client whose location matches the request's
 * `model`.
 *
 * The browser (proxy) and aistudio paths keep a single client — the proxy /
 * SDK pick the upstream location per request, so no per-model client is
 * needed there.
 *
 * CRITICAL: the ~95 existing `ai.models.generateContent({ model, ... })` call
 * sites stay UNCHANGED. We achieve this by exporting an `ai` facade whose
 * `.models` object transparently delegates each method to the per-location
 * client selected by `args.model`. The facade's `.models` is a plain mutable
 * object so the tracer (`installGeminiCallTracer`) can still wrap
 * `ai.models.generateContent` in place.
 */
const serverClientsByLocation = new Map<string, GoogleGenAI>();

function serverClientForLocation(location: string): GoogleGenAI {
  let client = serverClientsByLocation.get(location);
  if (!client) {
    client = new GoogleGenAI({
      vertexai: true,
      project: GCP_PROJECT,
      location,
      httpOptions: { timeout: SERVER_GENAI_TIMEOUT_MS },
    });
    serverClientsByLocation.set(location, client);
  }
  return client;
}

type ModelsApi = GoogleGenAI['models'];
type GenerateContentFn = ModelsApi['generateContent'];
type GenerateContentStreamFn = ModelsApi['generateContentStream'];
type EmbedContentFn = ModelsApi['embedContent'];

/** Pick the per-location server client's `.models` from a request's `model`. */
function serverModelsForModel(model: unknown): ModelsApi {
  const m = typeof model === 'string' ? model : '';
  return serverClientForLocation(locationForModel(m)).models;
}

/**
 * Build a `.models` facade that delegates the location-sensitive methods
 * (generateContent / generateContentStream / embedContent) to the right
 * per-location client by `params.model`, while forwarding every other member
 * (e.g. `list`, `countTokens`) to the base client unchanged.
 *
 * The wrappers take the SDK's native parameter types so call sites + the
 * telemetry tracer stay fully typed.
 */
function makeServerModelsFacade(baseModels: ModelsApi): ModelsApi {
  const facade = Object.create(baseModels) as ModelsApi & Record<string, unknown>;

  const modelsFor = (model: string) => BACKEND === 'vertex' ? serverModelsForModel(model) : baseModels;
  const inTransport = async <T>(
    params: { model: string; config?: { httpOptions?: { timeout?: number } } },
    call: () => Promise<T>,
    rpcKind: 'generation' | 'embedding' = 'generation',
  ): Promise<T> => {
    // Node-only module remains outside the browser bundle. The SDK has no
    // custom fetch option, so its stable fetch shim is scoped with ALS.
    const { withModelRequestTransport, linkObservedReportResponse } = await import('../server/modelRequestTransport');
    const invocationIds: string[] = [];
    const result = await withModelRequestTransport({
      backend: BACKEND,
      project: GCP_PROJECT,
      location: locationForModel(params.model),
      model: params.model,
      timeoutMs: params.config?.httpOptions?.timeout ?? SERVER_GENAI_TIMEOUT_MS,
      invocationIds,
      ...(rpcKind === 'embedding' ? { rpcKind, observationOnly: true } : {}),
    }, call);
    linkObservedReportResponse(result, invocationIds);
    return result;
  };
  const generateContent: GenerateContentFn = async (params) => {
    const normalized = normalizeArgonRequest(params);
    const result = await inTransport(normalized, () => modelsFor(normalized.model).generateContent(normalized));
    return assertArgonResponseComplete(normalized.model, result);
  };
  const generateContentStream: GenerateContentStreamFn = async (params) => {
    const normalized = normalizeArgonRequest(params, 'streaming');
    const stream = await inTransport(normalized, () => modelsFor(normalized.model).generateContentStream(normalized));
    // The SDK starts streaming/AFC requests lazily during iteration. Each
    // iterator operation must restore its own scope, even after another call.
    const scoped = Object.create(stream) as typeof stream;
    scoped.next = (...args) => inTransport(normalized, () => stream.next(...args));
    scoped.return = (...args) => inTransport(normalized, () => stream.return(...args));
    scoped.throw = (...args) => inTransport(normalized, () => stream.throw(...args));
    return guardArgonStream(normalized.model, scoped);
  };
  const embedContent: EmbedContentFn = (params) =>
    inTransport(params, () => modelsFor(params.model).embedContent(params), 'embedding');

  // Own, writable props so the telemetry tracer can re-wrap in place.
  Object.defineProperties(facade, {
    generateContent: { value: generateContent, writable: true, configurable: true, enumerable: true },
    generateContentStream: { value: generateContentStream, writable: true, configurable: true, enumerable: true },
    embedContent: { value: embedContent, writable: true, configurable: true, enumerable: true },
  });
  return facade;
}

type CachesApi = GoogleGenAI['caches'];

/**
 * Build a `.caches` facade whose `create` goes to the per-location client for
 * `params.model`. A cached-content resource lives in one location and can
 * only be referenced by a `generateContent` served from that same location,
 * so the cache must be created where the model is served (`global` for the
 * gemini-3.x families). Every other member (get, list, delete, …) is
 * forwarded to the base client unchanged.
 */
function makeServerCachesFacade(baseCaches: CachesApi): CachesApi {
  const facade = Object.create(baseCaches) as CachesApi;
  const create: CachesApi['create'] = (params) =>
    serverClientForLocation(locationForModel(params.model)).caches.create(params);
  Object.defineProperty(facade, 'create', {
    value: create, writable: true, configurable: true, enumerable: true,
  });
  return facade;
}

function makeBrowserModelsFacade(baseModels: ModelsApi): ModelsApi {
  const facade = Object.create(baseModels) as ModelsApi & Record<string, unknown>;
  const generateContent: GenerateContentFn = async (params) => {
    const normalized = normalizeArgonRequest(params);
    const response = await baseModels.generateContent(normalized);
    return assertArgonResponseComplete(normalized.model, response);
  };
  const generateContentStream: GenerateContentStreamFn = async (params) => {
    const normalized = normalizeArgonRequest(params, 'streaming');
    const stream = await baseModels.generateContentStream(normalized);
    return guardArgonStream(normalized.model, stream);
  };
  Object.defineProperties(facade, {
    generateContent: { value: generateContent, writable: true, configurable: true, enumerable: true },
    generateContentStream: { value: generateContentStream, writable: true, configurable: true, enumerable: true },
  });
  return facade;
}

/** Server requests get scoped transport; Vertex also gets per-location routing. */
function makeExportedClient(): GoogleGenAI {
  const base = makeGenAI();
  if (!isServerRuntime) {
    const facade = Object.create(base) as GoogleGenAI & Record<string, unknown>;
    Object.defineProperty(facade, 'models', {
      value: makeBrowserModelsFacade(base.models), writable: true, configurable: true, enumerable: true,
    });
    return facade;
  }

  // Seed the cache with the base-location client so any non-model member of
  // `.models` (list, countTokens, …) and `.models` prototype keep working.
  if (BACKEND === 'vertex') serverClientsByLocation.set(GCP_LOCATION, base);
  const facade = Object.create(base) as GoogleGenAI & Record<string, unknown>;
  Object.defineProperty(facade, 'models', {
    value: makeServerModelsFacade(base.models),
    writable: true,
    configurable: true,
    enumerable: true,
  });
  // `base.caches` is always present on the real SDK; the guard keeps module
  // load from throwing under SDK mocks that omit it.
  if (BACKEND === 'vertex' && base.caches) {
    Object.defineProperty(facade, 'caches', {
      value: makeServerCachesFacade(base.caches),
      writable: true,
      configurable: true,
      enumerable: true,
    });
  }
  return facade;
}

/** Singleton GoogleGenAI client used by every server-managed Gemini call. */
export const ai = makeExportedClient();

// R-11 — meter every generateContent call (idempotent wrapper).
void import('../lib/telemetry/tracedGeminiCall');

/** Re-exported for callers that build response schemas. */
export { Type } from '@google/genai';
export type { Schema } from '@google/genai';

/* ── runGeminiJSON ─────────────────────────────────────────────────── */

/**
 * BUG-4 FIX — default per-call timeout for runGeminiJSON, env-tunable so it can
 * be raised at deploy without a rebuild (`gcloud run ... --update-env-vars
 * GEMINI_JSON_TIMEOUT_MS=...`). gemini-2.5-pro structured-JSON on large SEDs
 * commonly runs 60-150s; 180s clears the realistic p99 of a SUCCESSFUL slow
 * call while still bounding a genuinely hung request well under the 420s
 * narrative ceiling. The prod "timed out after 60000ms" came from a deployed
 * revision predating the 120s bump — this raises the floor and makes it
 * adjustable. Explicit per-call overrides (the 30s/45s sub-stages) still win.
 */
const RUN_GEMINI_JSON_TIMEOUT_MS: number = (() => {
  const raw =
    typeof process !== 'undefined' ? Number(process.env?.GEMINI_JSON_TIMEOUT_MS) : NaN;
  return Number.isFinite(raw) && raw > 0 ? raw : 180_000;
})();

export interface RunGeminiJSONArgs {
  /** Propagated to the SDK transport and retry loop. */
  signal?: AbortSignal;
  /** Defaults to GEMINI_PRO_MODEL. Pass a specific model when you need it. */
  model?: string;
  contents: string;
  schema: Schema;
  /** Default GEMINI_JSON_TIMEOUT_MS env or 180000 (3 min). Long structured-JSON
   *  requests need this. */
  timeoutMs?: number;
  /** Optional config overrides. responseMimeType + responseSchema are
   *  always set by the helper to enforce JSON-mode. */
  configOverrides?: Record<string, unknown>;
  /**
   * Receives only token counts returned by Gemini's usageMetadata. The helper
   * never estimates missing values; absent provider metadata produces no
   * callback. `outputTokens` is the billed output: visible response
   * (`candidatesTokenCount`) plus thinking (`thoughtsTokenCount`).
   */
  onUsage?: (usage: GeminiMeasuredTokenUsage) => void;
  /**
   * Overrides for the transport retry loop (`withGenAIRetry`), spread over
   * the default `{ maxAttempts: 3 }`. Unset keeps the default for every
   * existing caller. DM2 doc matching passes `shouldRetry` so a proxy 429 is
   * not re-fired into the same closed IP bucket, and `onTransient` so its
   * throttle can count each retry as a request.
   */
  retry?: Pick<GenAIRetryOptions, 'maxAttempts' | 'shouldRetry' | 'onTransient'>;
}

// Defined in lib/billing/geminiUsage.ts (shared with the tracer and report
// telemetry); re-exported so existing importers keep their import path.
export type { GeminiMeasuredTokenUsage };

/**
 * Wrap a structured-JSON Gemini call with the standard config + timeout +
 * parse + minimum-shape guard.
 *
 * Returns the parsed JSON object. Throws on:
 *   - timeout (after `timeoutMs`),
 *   - empty response,
 *   - non-JSON response,
 *   - any thrown error from the SDK.
 */
export async function runGeminiJSON<T>(args: RunGeminiJSONArgs): Promise<T> {
  const {
    contents,
    schema,
    model = GEMINI_PRO_MODEL,
    timeoutMs = RUN_GEMINI_JSON_TIMEOUT_MS,
    configOverrides,
    signal,
    onUsage,
    retry,
  } = args;

  // BUG-4 FIX — wrap the SDK call in withGenAIRetry so a single undici
  // HeadersTimeoutError / "fetch failed" / 502-503 transport blip inside a
  // structured-JSON sub-stage no longer fails terminally with no backoff.
  // withGenAIRetry classifies 401/403/invalid/spend-cap as fatal (fail fast)
  // and only retries the transient classes, so genuine failures are NOT masked.
  // The app race-timeout is created FRESH per attempt and its timer is always
  // cleared (finally) so a resolved-before-timeout call leaves no dangling
  // libuv handle. Only the SDK call + race is inside the retry; the
  // empty-response and invalid-JSON guards live AFTER withGenAIRetry returns,
  // so a malformed payload is terminal (zero retries) — exactly the prior
  // behaviour. The retry only adds resilience to transport/timeout transients.
  const response = (await withGenAIRetry(
    async () => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const callPromise = ai.models.generateContent({
          model,
          contents,
          config: {
            responseMimeType: 'application/json',
            responseSchema: schema,
            ...(configOverrides ?? {}),
            abortSignal: signal,
          },
        });
        const timeoutPromise = new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error(`Gemini call timed out after ${timeoutMs}ms`)),
            timeoutMs,
          );
        });
        return await Promise.race([callPromise, timeoutPromise]);
      } finally {
        if (timer) clearTimeout(timer);
      }
    },
    { label: 'runGeminiJSON', maxAttempts: 3, ...retry, signal },
  )) as {
    text: string | (() => string);
    usageMetadata?: unknown;
  };

  throwIfAborted(signal);
  const measuredUsage = measuredGeminiTokenUsage(response.usageMetadata);
  if (measuredUsage) onUsage?.(measuredUsage);
  const text = typeof response.text === 'function' ? response.text() : response.text;
  if (!text) throw new Error('Gemini returned an empty response');

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(`Gemini returned invalid JSON: ${String(text).slice(0, 200)}`);
  }
}

// Type re-export so callers don't need a second import for the SDK type.
export { GoogleGenAI };
